/**
 * NOS-220: The ONE shared blog-post header, consumed by the public post page
 * (BlogPostPage), the dashboard preview (BlogPreviewPage), and the builder
 * canvas (BlogPostCanvas) so all three surfaces render the same presentation.
 *
 * Two variants:
 *  - cover image present → full-bleed hero: image background, dark gradient
 *    scrim, title + meta overlaid (mirrors the source-site treatment for
 *    imported posts). NOS-213 parity: imported heroes suppress the theme's
 *    org-logo overlay via `logo_hidden`; this header applies the same rule by
 *    design — it never renders an org logo.
 *  - no cover image → clean non-hero header (hand-authored posts without a
 *    cover keep a plain title + meta line; no regression).
 *
 * Meta line: author (when known) · published date · reading time · updated.
 * Category renders as a chip above the title (linked on the public page).
 */
import { Link } from "react-router-dom"
import { formatPostDate } from "@/lib/blog/postMeta"

export interface BlogPostHeaderProps {
  title: string
  coverImage?: string | null
  categoryName?: string | null
  categoryColor?: string | null
  /** When set, the category chip becomes a router link (public page). */
  categoryLinkTo?: string
  /**
   * Author byline — rendered only when known. Imported bylines are not yet
   * persisted (blog_posts has no author text column and author_id has no
   * publicly readable name source); the slot is ready for when storage lands.
   * See NOS-220 PR for the deferred author-storage decision.
   */
  authorName?: string | null
  publishedAt: string | null
  updatedAt?: string | null
  readingTimeMinutes: number
  primaryColor: string
}

function buildMetaParts({
  authorName,
  publishedAt,
  updatedAt,
  readingTimeMinutes,
}: Pick<BlogPostHeaderProps, "authorName" | "publishedAt" | "updatedAt" | "readingTimeMinutes">): string[] {
  const publishDate = formatPostDate(publishedAt)
  const updatedDate = formatPostDate(updatedAt ?? null)
  const showUpdated = Boolean(
    updatedAt && publishedAt && updatedAt.slice(0, 10) !== publishedAt.slice(0, 10)
  )
  const parts: string[] = []
  if (authorName) parts.push(`By ${authorName}`)
  if (publishDate) parts.push(publishDate)
  parts.push(`${readingTimeMinutes} min read`)
  if (showUpdated && updatedDate) parts.push(`Updated ${updatedDate}`)
  return parts
}

function MetaLine({
  parts,
  onImage,
}: {
  parts: string[]
  onImage: boolean
}) {
  return (
    <div
      className="mt-4 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm"
      style={{ color: onImage ? "rgba(255,255,255,0.88)" : "#64748b" }}
    >
      {parts.map((p, i) => (
        <span key={p} className="flex items-center gap-2">
          {i > 0 && (
            <span aria-hidden style={{ color: onImage ? "rgba(255,255,255,0.55)" : "#cbd5e1" }}>
              ·
            </span>
          )}
          <span>{p}</span>
        </span>
      ))}
    </div>
  )
}

export function BlogPostHeader({
  title,
  coverImage,
  categoryName,
  categoryColor,
  categoryLinkTo,
  authorName,
  publishedAt,
  updatedAt,
  readingTimeMinutes,
  primaryColor,
}: BlogPostHeaderProps) {
  const metaParts = buildMetaParts({ authorName, publishedAt, updatedAt, readingTimeMinutes })

  const chip = categoryName ? (
    <span
      className="inline-block rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white"
      style={{ backgroundColor: categoryColor ?? primaryColor }}
    >
      {categoryName}
    </span>
  ) : null
  const chipNode = chip && categoryLinkTo ? <Link to={categoryLinkTo}>{chip}</Link> : chip

  if (coverImage) {
    return (
      <header
        data-testid="blog-post-hero"
        className="relative flex min-h-[420px] w-full items-end overflow-hidden"
      >
        <img src={coverImage} alt="" className="absolute inset-0 h-full w-full object-cover" />
        {/* Scrim keeps the overlaid title/meta legible on any image. */}
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(15,23,42,0.25) 0%, rgba(15,23,42,0.45) 55%, rgba(15,23,42,0.78) 100%)",
          }}
        />
        <div className="relative z-10 mx-auto w-full max-w-3xl px-4 pb-10 pt-28 sm:px-6 lg:px-8">
          {chipNode && <div className="mb-4">{chipNode}</div>}
          <h1
            className="font-bold text-white"
            style={{ fontSize: "clamp(2rem, 4vw, 3rem)", lineHeight: 1.1, margin: 0 }}
          >
            {title}
          </h1>
          <MetaLine parts={metaParts} onImage />
        </div>
      </header>
    )
  }

  return (
    <header
      data-testid="blog-post-header"
      className="mx-auto w-full max-w-3xl px-4 pb-2 pt-10 sm:px-6 lg:px-8"
    >
      {chipNode && <div className="mb-4">{chipNode}</div>}
      <h1
        className="font-bold"
        style={{ fontSize: "clamp(2rem, 4vw, 3rem)", lineHeight: 1.1, margin: 0, color: "#0f172a" }}
      >
        {title}
      </h1>
      <MetaLine parts={metaParts} onImage={false} />
    </header>
  )
}
